export const LOADING_TEXTS = [
  // --- CATEGORY 1: LIGHTING MASTERY ---
  "Let's talk about lighting—it changes the entire mood of your scene.",
  'Try "Golden Hour" in your next prompt for warm, cinematic natural light.',
  'Use "Volumetric Fog" to add those moody, atmospheric light beams.',
  'Words like "Bioluminescent" create harsh, vibrant contrast.',
  'Great lighting stops your video from looking flat or fake.',

  // --- CATEGORY 2: CAMERA CONTROL ---
  'You are the Director. Tell the camera exactly where to stand.',
  'A "Low Angle" shot makes your subject look powerful and dominant.',
  'Use "Drone Flyover" to establish massive landscapes or cities.',
  '"Extreme Close-up" captures emotional details like eyes or hands.',
  "Guide the audience's eye with specific angles.",

  // --- CATEGORY 3: VISUAL STYLE ---
  "You don't have to stick to the default 'AI look'. Pick a specific style.",
  '"35mm Film Grain" gives that classic, textured Hollywood movie vibe.',
  '"Studio Ghibli Style" creates lush, colorful 2D animated worlds.',
  '"Photorealistic 8k" pushes the model for maximum real-world detail.',
  'Defining a style is the best way to make your video stand out.',

  // --- CATEGORY 4: MOTION & PHYSICS ---
  'Motion controls the energy. How fast should the world move?',
  '"Slow Motion" makes action scenes feel heavier and more epic.',
  '"Timelapse" for moving clouds, busy traffic, or growing plants.',
  '"Smooth Tracking" keeps the camera locked steadily on your subject.',
  'Speed changes how the audience feels about the action on screen.',

  // --- CATEGORY 5: SUBJECT DETAILS ---
  'The AI needs details. Stop it from guessing what your subject looks like.',
  "Describe textures, like 'worn leather', 'silk', or 'rusted metal'.",
  "Use strong emotion keywords like 'joyful', 'stoic', or 'terrified'.",
  'Specify clothing era and fit to keep your characters consistent.',
  'The more specific your adjectives, the less random the final result.',

  // --- CATEGORY 6: PRO WORKFLOWS (The Upsell) ---
  'Become a Pro creator - use advanced prompts for high quality.',
  'Upscaling to 1080p sharpens the edges and removes that digital blur.',
  'High frame rates (60fps) make movement look fluid and expensive.',
  'Pros usually run 3 variations of the same prompt to pick the best seed.',
  'Your video is just about ready. Get ready to watch it..',
];

// Total simulation animation duration (progress bar goes 0→100 over this time)
export const SIMULATION_DURATION = 150 * 1000; // 150 seconds

// Time after which client-side DB polling begins (while simulation animation continues)
export const POLLING_START_DELAY = SIMULATION_DURATION * 0.5;

// Pause duration shown at 100% before the countdown loops back to 0
export const COUNTDOWN_LOOP_PAUSE_MS = 1000; // 1 second

export const PRESET_PROMPTS: Record<string, string> = {
  'start-frame':
    'Provided image is start frame. Follow the visual style, lighting, and content of the image exactly, creating a seamless continuation of the scene.',
  archival:
    'Shot on Eastmann 100t film, the image quality is grainy and high contrast, with shallow depth of field and cinematic look, epic and dramatic shot, very nostalgic.',
  'film-noir':
    'Theme: Film Noir\nColor: High contrast black and white, deep shadows with selective highlights.\nCamera: Arri Alexa Mini, RED Monochrome, or vintage 35mm cameras.\nFilm Stock: Ilford HP5, Kodak Double-X, or high-contrast digital monochrome settings.\nLighting: Low key, chiaroscuro lighting with hard shadows, venetian blind effects, and strong backlighting.\nVibe: Moody, mysterious, suspenseful',
  cardboard:
    'Theme: Cardboard & Papercraft\nColor: Earthy tones like brown, beige, and muted pastels, with occasional pops of color to simulate colored paper.\nFilm Stock: analog film\nLighting: Soft, diffused lighting\nContent Transformation: Everything in the scene—from characters to objects and scenery—should be transformed into cardboard, paper, and glue. Elements should have visible creases, folds, and textures resembling handcrafted models.',
  'stop-motion':
    'Theme: Playful handcrafted animation.\nColor: Bright, saturated primary colors with handcrafted textures.\nFilm Stock: Smooth frame-by-frame animation with visible stop-motion quirks.\nLighting: Controlled spotlights with small shadows to highlight miniature craftsmanship.\nVibe: Quirky, charming, family-friendly',
  balloon:
    'Theme: Everything is inflated like a balloon.\nColor: Glossy, bright colors—reds, yellows, blues, and metallics like gold and silver.\nFilm Stock: Clean digital with exaggerated reflections on shiny surfaces.\nLighting: High-key lighting with glossy highlights to mimic rubbery textures.\nContent Transformation: All characters, objects, and environments look inflated, with visible seams and a bouncy quality.\nVibe: Fun, surreal, viral-ready',
};

export const CAMERA_OPTIONS: Record<string, string> = {
  'fixed-camera':
    'Static camera: The camera remains completely stationary with no movement, panning, or zooming',
  'slow-pan': 'Slow Pan (left/right)',
  'dolly-in': 'Dolly In (zoom towards subject)',
  'dolly-out': 'Dolly Out (zoom away)',
  orbit: 'Orbit (circle around subject)',
  'crane-up': 'Crane Up (rising shot)',
  'crane-down': 'Crane Down (descending shot)',
  tracking: 'Tracking Shot (follow subject)',
  handheld: 'Handheld (natural shake)',
};

export const LIGHTING_OPTIONS: Record<string, string> = {
  'golden-hour': 'Golden Hour (warm sunset)',
  studio: 'Studio Lighting (professional)',
  neon: 'Neon/Urban Night',
  dramatic: 'Dramatic Shadows',
  soft: 'Soft Diffused Light',
  backlit: 'Backlit (silhouette)',
  'natural-window': 'Natural Window Light',
};

export const MOTION_OPTIONS: Record<string, string> = {
  'slow-motion': 'Slow Motion',
  'fast-motion': 'Fast Forward / Timelapse',
  'smooth-tracking': 'Smooth Subject Tracking',
  'cinematic-pan': 'Cinematic Slow Pan',
  handheld: 'Dynamic Handheld (Shake)',
  'drone-reveal': 'Aerial Drone Reveal',
  'dolly-zoom': 'Dolly Zoom (Vertigo Effect)',
  'whip-pan': 'Whip Pan Transition',
  orbit: '360 Orbit / Spin',
  pov: 'First Person POV',
  reverse: 'Reverse Motion',
  'bullet-time': 'Bullet Time Freeze Frame',
  glitch: 'Glitch / Stutter Motion',
  'stop-motion': 'Stop Motion Animation',
  hyperlapse: 'Hyperlapse Journey',
  'crane-shot': 'Cinematic Crane Shot',
  spiral: 'Spiral Up / Down',
  jolt: 'Sudden Jolt / Impact',
  vibration: 'Subtle Vibration',
};

export const AUDIO_OPTIONS: Record<string, string> = {
  'no-audio': 'Silent video with no audio or background sound',
  cinematic: 'Epic Cinematic Score',
  'ambient-nature': 'Calm Nature Ambience',
  lofi: 'Lofi Chill Beats',
  cyberpunk: 'Cyberpunk Synthwave',
  orchestral: 'Emotional Orchestral',
  'upbeat-pop': 'Upbeat Pop / Electronic',
  suspenseful: 'Suspenseful / Thriller',
  acoustic: 'Acoustic Folk',
  'corporate-tech': 'Corporate Tech / Upbeat',
  asmr: 'ASMR / Quiet Whispers',
  'vintage-vinyl': 'Vintage Vinyl Crackle',
  'sci-fi': 'Sci-Fi / Space Ambience',
  'action-movie': 'Action Movie Intensity',
  'jazz-lounge': 'Jazz Lounge',
  horror: 'Creepy Horror Atmosphere',
  industrial: 'Industrial Ambient',
  minimalist: 'Minimalist Piano',
  'rock-anthemic': 'Rock / Anthemic',
};

export const DIALOGUE_OPTIONS: Record<string, string> = {
  'no-dialogue': 'No dialogue, narration, or speaking voices in the video',
  'accent:-united-states': 'Dialogue: Speaking with a clear American accent',
  'accent:-united-kingdom': 'Dialogue: Speaking with a British accent',
  'accent:-australia': 'Dialogue: Speaking with an Australian accent',
  'accent:-india': 'Dialogue: Speaking with an Indian accent',
  'whispered-dialogue': 'Dialogue: Whispered secrets and quiet murmurs',
  'shouted-dialogue': 'Dialogue: Loud shouting and intense vocal delivery',
  'monologue-emotional': 'Dialogue: A deep, emotional internal monologue',
  'fast-paced-chatter': 'Dialogue: Rapid-fire, fast-paced conversation',
};

export const GRAPHICS_OPTIONS: Record<string, string> = {
  'no-graphics': 'No graphics, text overlays, or subtitles on screen',
  'subtitles-lower': 'Graphics: Minimalist white subtitles in the lower third',
  'title-card': 'Graphics: Bold, centered cinematic title card',
  'social-overlay': 'Graphics: Modern social media UI elements overlay',
  'data-vfx': 'Graphics: Holographic data displays and digital readouts',
  'vintage-timestamp': 'Graphics: Retro VHS-style date and time stamp in corner',
};

// --- IMAGE ENHANCEMENT OPTIONS ---
export const IMAGE_STYLE_OPTIONS: Record<string, string> = {
  photorealistic: 'Style: Ultra-photorealistic, highly detailed, 8k resolution, sharp focus',
  cinematic: 'Style: Cinematic photography, movie still, dramatic lighting, anamorphic lens',
  anime:
    'Style: High-quality anime artwork, Studio Ghibli style, vibrant colors, detailed line art',
  'digital-art':
    'Style: Masterpiece digital art, trending on ArtStation, vivid colors, intricate details',
  watercolor: 'Style: beautiful watercolor painting, soft edges, ethereal, flowing colors',
  'oil-painting': 'Style: Classic oil painting, rich impasto texture, dramatic brushstrokes',
  '3d-render': 'Style: 3D render, Octane render, Unreal Engine 5, ray tracing, volumetric lighting',
  'retro-vintage': 'Style: Vintage aesthetic, retro 80s synthwave, neon colors, VHS glitch effect',
};

export const IMAGE_LIGHTING_OPTIONS: Record<string, string> = {
  'golden-hour': 'Lighting: Golden hour light, warm sun rays, long shadows',
  studio: 'Lighting: Professional studio lighting, rim light, softbox, clean background',
  neon: 'Lighting: Cyberpunk neon lighting, vibrant pinks and blues, glowing reflections',
  dramatic: 'Lighting: High contrast dramatic lighting, chiaroscuro, deep shadows',
  soft: 'Lighting: Soft diffused lighting, overcast day, gentle ambient light',
  backlit: 'Lighting: Strong backlight, silhouette effect, glowing edges',
  volumetric: 'Lighting: Volumetric lighting, god rays piercing through dust/fog',
};

export const IMAGE_COMPOSITION_OPTIONS: Record<string, string> = {
  'close-up': 'Composition: Extreme close-up macro shot, intricate details, bokeh background',
  portrait: 'Composition: Professional portrait framing, eye-level, shallow depth of field',
  'wide-angle': 'Composition: Wide-angle lens, expansive landscape, epic scale',
  'bird-eye': "Composition: Bird's-eye view, shot from directly above",
  'low-angle': 'Composition: Low-angle shot, looking up to make subject look imposing',
  'rule-of-thirds': 'Composition: Balanced rule of thirds framing',
  symmetrical: 'Composition: Perfectly symmetrical framing, Wes Anderson style',
};

export const IMAGE_MOOD_OPTIONS: Record<string, string> = {
  'dark-moody': 'Mood: Dark, moody, mysterious atmosphere, desaturated colors',
  'bright-cheerful': 'Mood: Bright, cheerful, airy, vibrant, optimistic atmosphere',
  ethereal: 'Mood: Ethereal, dreamy, surreal, magical, glowing aura',
  cyberpunk: 'Mood: Cyberpunk, gritty, futuristic, dystopian, high-tech',
  romantic: 'Mood: Romantic, soft, intimate, warm tones',
  epic: 'Mood: Epic, monumental, awe-inspiring, grand scale',
};

// Per-model configuration for supported settings
export const MODEL_CONFIG: Record<
  string,
  {
    ratios: string[];
    resolutions: string[];
    durations: string[];
    outputFormats?: string[];
    defaultRatio: string;
    defaultResolution: string;
    defaultDuration: string;
    defaultFormat?: string;
  }
> = {
  'sora-2': {
    ratios: ['9:16', '16:9'],
    resolutions: ['720p'],
    durations: ['10s', '15s'],
    defaultRatio: '16:9',
    defaultResolution: '720p',
    defaultDuration: '10s',
  },
  'sora-2-pro': {
    ratios: ['9:16', '16:9'],
    resolutions: ['720p', '1080p'],
    durations: ['10s', '15s'],
    defaultRatio: '16:9',
    defaultResolution: '1080p',
    defaultDuration: '10s',
  },
  'nano-banana-2': {
    ratios: [
      '1:1',
      '1:4',
      '1:8',
      '2:3',
      '3:2',
      '3:4',
      '4:1',
      '4:3',
      '4:5',
      '5:4',
      '8:1',
      '9:16',
      '16:9',
      '21:9',
      'auto',
    ],
    resolutions: ['1K', '2K', '4K'],
    durations: ['10s'],
    outputFormats: ['jpg', 'png'],
    defaultRatio: 'auto',
    defaultResolution: '1K',
    defaultDuration: '10s',
    defaultFormat: 'jpg',
  },
  'z-image': {
    ratios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    resolutions: [],
    durations: [],
    defaultRatio: '16:9',
    defaultResolution: '',
    defaultDuration: '',
  },
  'elevenlabs-sfx': {
    ratios: ['9:16', '16:9'],
    resolutions: [],
    durations: ['0.5s', '1s', '2s'],
    defaultRatio: '',
    defaultResolution: '',
    defaultDuration: '0.5s',
  },
  runway: {
    ratios: ['1:1', '4:3', '3:4', '16:9', '9:16'],
    resolutions: ['720p', '1080p'],
    durations: ['5s', '10s'],
    defaultRatio: '16:9',
    defaultResolution: '720p',
    defaultDuration: '5s',
  },
  'grok-imagine': {
    ratios: ['16:9', '9:16', '1:1', '2:3', '3:2'],
    resolutions: ['480p', '720p'],
    durations: ['6s', '10s', '15s'],
    defaultRatio: '16:9',
    defaultResolution: '480p',
    defaultDuration: '6s',
  },
};
